import React, { useState, useEffect } from 'react';
import { Input } from 'antd';
import styled from '@emotion/styled';
interface IProps {
    minVal: number | null;
    maxVal: number | null;
    onMaxChange: (val: number) => void;
    onMinChange: (val: number) => void;
    parentClassName?: string;
    inputClassName?: string;
    placeHolder?: [string | undefined, string | undefined];
    disabled?: boolean;
}

const StyledInput = styled(Input)`
    width: 120px;
    height: 32px;
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    appearance: textfield;
    border-radius: 0.375rem;
`;

// Function to allow only numbers and dots
const allowOnlyNumbersAndDot = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;
    // Allow: backspace, delete, tab, escape, enter, decimal point, and numbers
    if (
        key === 'Backspace' ||
        key === 'Delete' ||
        key === 'Tab' ||
        key === 'Escape' ||
        key === 'Enter' ||
        key === '.' ||
        key === 'ArrowLeft' ||
        key === 'ArrowRight' ||
        key === 'ArrowUp' ||
        key === 'ArrowDown' ||
        key === 'Home' ||
        key === 'End' ||
        e.ctrlKey ||
        e.metaKey || // Allow Ctrl/Cmd + A, C, V, etc.
        /^[0-9]$/.test(key)
    ) {
        return true;
    }
    e.preventDefault();
    return false;
};

const RangeInputCustom: React.FC<IProps> = ({
    minVal,
    maxVal,
    onMaxChange,
    onMinChange,
    parentClassName = '',
    inputClassName,
    placeHolder = ['Min', 'Max'],
    disabled = false,
}) => {
    const [minInput, setMinInput] = useState<string>(minVal != null ? String(minVal) : '');
    const [maxInput, setMaxInput] = useState<string>(maxVal != null ? String(maxVal) : '');

    // Sync internal state with props when they change externally
    useEffect(() => {
        setMinInput(minVal != null ? String(minVal) : '');
    }, [minVal]);

    useEffect(() => {
        setMaxInput(maxVal != null ? String(maxVal) : '');
    }, [maxVal]);

    const handleMinChange = (value: string) => {
        setMinInput(value);
        // Convert to number only if value is valid, otherwise pass null
        if (value === '' || value === '.') {
            onMinChange(null as any);
        } else {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
                onMinChange(numValue);
            }
        }
    };

    const handleMaxChange = (value: string) => {
        setMaxInput(value);
        // Convert to number only if value is valid, otherwise pass null
        if (value === '' || value === '.') {
            onMaxChange(null as any);
        } else {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
                onMaxChange(numValue);
            }
        }
    };

    return (
        <div className={`flex justify-between items-center ${parentClassName}`}>
            <StyledInput
                type="text"
                value={minInput}
                onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty string, numbers, and single dot
                    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                        handleMinChange(value);
                    }
                }}
                onKeyDown={allowOnlyNumbersAndDot}
                placeholder={placeHolder[0]}
                className={inputClassName}
                disabled={disabled}
            />
            -
            <StyledInput
                type="text"
                value={maxInput}
                onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty string, numbers, and single dot
                    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                        handleMaxChange(value);
                    }
                }}
                onKeyDown={allowOnlyNumbersAndDot}
                placeholder={placeHolder[1]}
                className={inputClassName}
                disabled={disabled}
            />
        </div>
    );
};

export default RangeInputCustom;
